import React, {useEffect} from "react";
import {
    Box,
    Button, FormField,
    Modal, SpaceBetween, Textarea, Tiles, Autosuggest, ColumnLayout
} from "@amzn/awsui-components-react";
import {ACTIONS} from "src/services/calculation-builder/CalculationStepsReducer";
import {Alert, Popover} from "@amzn/awsui-components-react/polaris";
import {
    SetUpTagJournalEntryModalProps
} from "src/models/calculation-builder/je-tag/SetUpTagJournalEntryModalPropsInterface";
import { CalculationBuilderContext } from '../CalculationBuilderView';
import { JeTagType } from "src/utils/constants";
import { initialState } from "./SetUpTagJournalEntryModalState";
import { modalReducer, MODAL_ACTIONS } from "./SetUpTagJournalEntryModalReducer";
import useReducerWithLogger from "src/services/utils/ReducerWithLogger";
import {ACTIONS as CALC_BUILDER_ACTIONS} from "src/services/calculation-builder/CalculationBuilderReducer";

export default function SetUpTagJournalEntryModal (props: SetUpTagJournalEntryModalProps) {
    const { services, showTagJournalEntryModal, jeTagBeingEdited , dispatch, selectedStepForJETag, isSaving = false, stepError, cliHasBeatQualifier = false} = props;

    const { calcBuilderState, calcBuilderDispatch } = React.useContext(CalculationBuilderContext);
    const { calculation, selectedCalculationVersion } = calcBuilderState;

    const [modalState, modalDispatch] = useReducerWithLogger(modalReducer, initialState);
    const [showUntagError, setShowUntagError] = React.useState(false);

    const [jeTagsResult, jeTagsLoading, jeTagsError] = services.calculationStepsService.getActiveJeTags(calculation?.calculationNumber, selectedCalculationVersion, modalState.shouldFetchJeTags);
    const [unTagJeTagsResult, unTagJeTagsLoading, unTagJeTagsError] = services.calculationStepsService.unTagJEFromCalculationStep(selectedStepForJETag?.calculationNumber, selectedStepForJETag?.stepId, modalState.shouldUnTagJeTags);

    const isJETagInActive = selectedStepForJETag?.jeTagActive === false
    const isInactiveMarkupTag = selectedStepForJETag?.jeTagType === JeTagType.MARKUP && isJETagInActive

    useEffect(() => {
        // Clear error display when modal opens to reset stale errors
        if (showTagJournalEntryModal) {
            setShowUntagError(false);
        }
    }, [showTagJournalEntryModal]);

    useEffect(() => {
        const defaultJETagType = cliHasBeatQualifier ? JeTagType.BASE : null
        
        // Update description when selected step changes
        modalDispatch(MODAL_ACTIONS.RESET_FOR_STEP.withPayload({
            description: selectedStepForJETag?.jeTagDescription || '',
            jeTagType: selectedStepForJETag?.jeTagType || defaultJETagType,
            associatedBaseJE: selectedStepForJETag?.jeTagId || '',
        }));

        if (!!(selectedStepForJETag?.jeTagId && selectedStepForJETag?.jeTagDescription)) {
            dispatch(ACTIONS.JE_TAG_BEING_EDITED.withPayload(true));
        } else {
            dispatch(ACTIONS.JE_TAG_BEING_EDITED.withPayload(false));
        }
    }, [selectedStepForJETag]);

    useEffect(() => {
        // Trigger API call when modal opens for "Tag a journal Entry"
        if (showTagJournalEntryModal && !jeTagBeingEdited && cliHasBeatQualifier) {
            modalDispatch(MODAL_ACTIONS.SET_SHOULD_FETCH_JE_TAGS.withPayload(true));
        } else {
            modalDispatch(MODAL_ACTIONS.SET_SHOULD_FETCH_JE_TAGS.withPayload(false));
        }
    }, [showTagJournalEntryModal, jeTagBeingEdited, cliHasBeatQualifier]);

    useEffect(() => {
        if (jeTagsError) {
            console.error('Failed to fetch JE tags:', jeTagsError);
        } else if (jeTagsResult) {
            const formattedOptions = jeTagsResult.JETags
                .filter(item => item.type === JeTagType.BASE)
                .map(item => ({
                    label: `JE ${item.id} - ${item.description}`,
                    value: item.id.toString()
                }));
            modalDispatch(MODAL_ACTIONS.SET_BASE_JE_OPTIONS.withPayload(formattedOptions));
        }
    }, [jeTagsResult, jeTagsError]);

    useEffect(() => {
        // Reset associated base JE when switching to BASE role
        if (modalState.beatCliRole === JeTagType.BASE) {
            modalDispatch(MODAL_ACTIONS.RESET_BASE_JE.withPayload(null));
        }
    }, [modalState.beatCliRole]);

    useEffect(() => {
        if (unTagJeTagsError) {
            setShowUntagError(true);
            modalDispatch(MODAL_ACTIONS.SET_SHOULD_UNTAG_JE_TAGS.withPayload(false));
        } else if (unTagJeTagsResult) {
            setShowUntagError(false);
            modalDispatch(MODAL_ACTIONS.SET_SHOULD_UNTAG_JE_TAGS.withPayload(false));
            dispatch(ACTIONS.TAG_JOURNAL_ENTRY_MODAL.withPayload(false));
            calcBuilderDispatch(CALC_BUILDER_ACTIONS.SET_REFRESH_CALC_STEPS_FLAG.withPayload(true));
        }
    }, [unTagJeTagsResult, unTagJeTagsError]);

    const closeModal = () => {
        if (stepError) {
            // Restore original description
            dispatch(ACTIONS.UPDATE_CURRENT_STEP.withPayload({
                field: 'jeTagDescription',
                value: modalState.originalJETagDescription === '' ? null : modalState.originalJETagDescription
            }));
            modalDispatch(MODAL_ACTIONS.SET_DESCRIPTION.withPayload(modalState.originalJETagDescription));
        }
        dispatch(ACTIONS.TAG_JOURNAL_ENTRY_MODAL.withPayload(false));
    }

    const saveJETag = () => {
        dispatch(ACTIONS.SET_STEP_BEING_EDITED.withPayload(selectedStepForJETag));

        // Build fields array for batch update
        const fieldsToUpdate: Array<{ field: string; value: any }> = [
            { field: 'jeTagDescription', value: modalState.jeTagDescription }
        ];

        if (cliHasBeatQualifier) {
            fieldsToUpdate.push({ field: 'jeTagType', value: modalState.beatCliRole });

            if (modalState.beatCliRole === JeTagType.MARKUP && modalState.associatedBaseJE) {
                const jeTagId = parseInt(modalState.associatedBaseJE);
                if (!isNaN(jeTagId)) {
                    fieldsToUpdate.push({ field: 'jeTagId', value: jeTagId });
                }
            }
        }

        // Single dispatch call with multiple fields
        dispatch(ACTIONS.UPDATE_CURRENT_STEP.withPayload({
            fields: fieldsToUpdate
        }));
    }

    const isSaveDisabled = () => {
        return !modalState.jeTagDescription.trim() ||
            modalState.jeTagDescription === modalState.originalJETagDescription ||
            isJETagInActive ||
            (modalState.beatCliRole === JeTagType.MARKUP && !modalState.associatedBaseJE);
    };

    const handleToggleJEActive = () => {
        const newActiveState = !selectedStepForJETag?.jeTagActive;
        const isDeactivating = selectedStepForJETag?.jeTagActive === true;

        if (isDeactivating) {
            const baseMessage = "Are you sure you want to deactivate the Journal Entry tag?";
            const additionalMessage = cliHasBeatQualifier ? "\n\nAssociated Markup/Base Step to this JE Tag will be deactivated as well." : "";

            const confirmed = window.confirm(baseMessage + additionalMessage);
            if (!confirmed) {
                return;
            }
        }

        dispatch(ACTIONS.SET_STEP_BEING_EDITED.withPayload(selectedStepForJETag));
        dispatch(ACTIONS.UPDATE_CURRENT_STEP.withPayload({
            field: 'jeTagActive',
            value: newActiveState
        }));
        dispatch(ACTIONS.ASSOCIATED_TAG_NEED_REFRESH.withPayload(true));
    }

    const handleUntagJE = () => {
        const baseMessage = "Are you sure you want to unTag the Journal Entry tag?";
        const additionalMessage = cliHasBeatQualifier ? "\n\nAssociated Markup/Base Step to this JE Tag will be unTagged as well." : "";

        const confirmed = window.confirm(baseMessage + additionalMessage);
        if (!confirmed) {
            return;
        }

        modalDispatch(MODAL_ACTIONS.SET_SHOULD_UNTAG_JE_TAGS.withPayload(true));
    }

    const jeTagActivationButton = (
        <Button
            variant="normal"
            onClick={handleToggleJEActive}
            disabled={isSaving || isInactiveMarkupTag}
        >
            {selectedStepForJETag?.jeTagActive ? "Deactivate JE Tag" : "Activate JE Tag"}
        </Button>
    );

    return <Modal className="setUpTagJournalEntryModal"
                  visible={showTagJournalEntryModal}
                  size="medium"
                  header={jeTagBeingEdited ? "Edit Journal Entry Tag" : "Tag a Journal Entry"}
                  onDismiss={closeModal}
                  footer={
                      <Box float="right">
                          <SpaceBetween direction="horizontal" size="xs">
                              <Button variant="link" onClick={closeModal} disabled={isSaving}>
                                  Cancel
                              </Button>
                              {jeTagBeingEdited && (
                                  <>
                                      {isInactiveMarkupTag ? (
                                          <Popover size="small" position="top" content="Activate the corresponding Base step">
                                              {jeTagActivationButton}
                                          </Popover>
                                      ) : (
                                          jeTagActivationButton
                                      )}

                                      <Button
                                          variant="normal"
                                          onClick={handleUntagJE}
                                          disabled={isSaving || unTagJeTagsLoading}
                                      >
                                          UnTag JE
                                      </Button>
                                  </>
                              )}
                              <Button variant="primary"
                                      onClick={saveJETag}
                                      loading={isSaving}
                                      disabled={isSaveDisabled()}
                              >
                                  {isSaving ? "Saving..." : "Confirm"}
                              </Button>
                          </SpaceBetween>
                      </Box>
                  }>
        <div className="setUpTagJournalEntryModalContainer">
            <SpaceBetween direction="vertical" size="m">
                {jeTagBeingEdited && selectedStepForJETag?.jeTagId && (
                    <>
                        {cliHasBeatQualifier ? (
                            <ColumnLayout columns={2} variant="text-grid">
                                <FormField label="Journal Entry Unique Identifier" stretch>
                                    <div>
                                        JE {selectedStepForJETag.jeTagId}
                                    </div>
                                </FormField>
                                <FormField label="BEAT CLI Role" stretch>
                                    <div>
                                        {selectedStepForJETag.jeTagType}
                                    </div>
                                </FormField>
                            </ColumnLayout>
                        ) : (
                            <FormField label="Journal Entry Unique Identifier" stretch>
                                <div>
                                    JE {selectedStepForJETag.jeTagId}
                                </div>
                            </FormField>
                        )}
                    </>
                )}

                {!jeTagBeingEdited && cliHasBeatQualifier && (
                    <>
                        <FormField label="BEAT CLI Role" stretch>
                            <Tiles
                                onChange={({ detail }) => modalDispatch(MODAL_ACTIONS.SET_BEAT_CLI_ROLE.withPayload(detail.value as JeTagType))}
                                value={modalState.beatCliRole}
                                items={[
                                    {
                                        label: JeTagType.BASE,
                                        value: JeTagType.BASE
                                    },
                                    {
                                        label: JeTagType.MARKUP,
                                        value: JeTagType.MARKUP
                                    }
                                ]}
                            />
                        </FormField>

                        <FormField
                            label="Associated Base Journal Entries"
                            description="Link this Markup with an existing Base JE Tagged Step"
                            stretch
                        >
                            <Autosuggest
                                onSelect={({ detail }) => modalDispatch(MODAL_ACTIONS.SET_ASSOCIATED_BASE_JE.withPayload(detail.value))}
                                value={modalState.associatedBaseJE}
                                options={modalState.baseJEOptions}
                                placeholder="Search journal entry ID"
                                disabled={modalState.beatCliRole === JeTagType.BASE}
                                statusType={jeTagsLoading ? "loading" : "finished"}
                                filteringType="auto"
                                empty={modalState.baseJEOptions.length === 0 ? "No Base JE Tags available. Please create a Base JE Tag first." : undefined}
                            />
                        </FormField>
                    </>
                )}

                <FormField label="Description" stretch>
                    <Textarea
                        className='journalEntryDescription'
                        onChange={({ detail }) => modalDispatch(MODAL_ACTIONS.SET_DESCRIPTION.withPayload(detail.value))}
                        value={modalState.jeTagDescription}
                        placeholder="Enter description for journal entry"
                        disabled={isJETagInActive}
                        readOnly={isJETagInActive}
                    />
                </FormField>

                {[stepError, (showUntagError ? unTagJeTagsError : null)].filter(Boolean).map((error, index) => (
                    <Alert key={index} type="error" dismissible={false}>{error}</Alert>
                ))}
            </SpaceBetween>
        </div>

    </Modal>
}

